import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, FileSignature, CheckCircle2, XCircle, ArrowRight, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { CompanyContext, useCurrency, formatCurrency } from '../contexts/CompanyContext';
import RowActions from '../components/RowActions';
import BuildingFilter from '../components/BuildingFilter';
import { generateQuotationPDF } from '../lib/quotationPdf';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-700',
  invoiced: 'bg-indigo-100 text-indigo-800',
};

const blank = (direction) => ({
  direction,
  title: '',
  building_id: '',
  client_id: '',
  supplier_id: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  issue_date: new Date().toISOString().slice(0, 10),
  valid_until: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  line_items: [{ item_name: '', description: '', quantity: 1, unit: 'pcs', unit_price: 0, discount_pct: 0, tax_pct: 5 }],
  terms: 'Payment within 30 days. Prices are exclusive of VAT unless stated.',
  scope_summary: '',
  currency: 'AED',
});

export default function Quotations() {
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const { symbol } = useCurrency();

  const [direction, setDirection] = useState('sales');
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [draft, setDraft] = useState(blank('sales'));
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const canManage = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'CS_SUPERVISOR', 'PROCUREMENT_OFFICER'].includes(user?.role);
  const canApprove = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { direction };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (filterBuildingId !== 'all') params.building_id = filterBuildingId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [q, b, s, t] = await Promise.all([
        axios.get(`${API}/quotations`, { params, withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/tenants`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setItems(q.data || []);
      setBuildings(b.data || []);
      setSuppliers(s.data || []);
      setTenants(t.data || []);
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 403) {
        toast.error('Quotations require the Enterprise plan.');
      } else {
        toast.error('Failed to load quotations');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [direction, statusFilter, filterBuildingId, dateFrom, dateTo]);

  const totals = useMemo(() => {
    let st = 0, dc = 0, tx = 0;
    (draft.line_items || []).forEach(li => {
      const g = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
      const d = g * ((parseFloat(li.discount_pct) || 0) / 100);
      const t = (g - d) * ((parseFloat(li.tax_pct) || 0) / 100);
      st += g; dc += d; tx += t;
    });
    return { subtotal: st, discount: dc, tax: tx, grand: st - dc + tx };
  }, [draft.line_items]);

  const openNew = () => { setEditing(null); setDraft(blank(direction)); setShowDialog(true); };
  const openEdit = (q) => {
    setEditing(q.id);
    setDraft({
      ...q,
      issue_date: q.issue_date?.slice(0, 10),
      valid_until: q.valid_until?.slice(0, 10),
      line_items: q.line_items?.length ? q.line_items : blank(direction).line_items,
    });
    setShowDialog(true);
  };
  const addLine = () => setDraft({ ...draft, line_items: [...draft.line_items, { item_name: '', description: '', quantity: 1, unit: 'pcs', unit_price: 0, discount_pct: 0, tax_pct: 5 }] });
  const updateLine = (i, k, v) => setDraft({ ...draft, line_items: draft.line_items.map((li, idx) => idx === i ? { ...li, [k]: v } : li) });
  const removeLine = (i) => setDraft({ ...draft, line_items: draft.line_items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!draft.title) { toast.error('Title is required'); return; }
    const payload = {
      ...draft,
      issue_date: draft.issue_date ? new Date(draft.issue_date).toISOString() : null,
      valid_until: draft.valid_until ? new Date(draft.valid_until).toISOString() : null,
      line_items: draft.line_items.map(l => ({
        ...l,
        quantity: parseFloat(l.quantity) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
        discount_pct: parseFloat(l.discount_pct) || 0,
        tax_pct: parseFloat(l.tax_pct) || 0,
      })),
    };
    try {
      if (editing) {
        await axios.put(`${API}/quotations/${editing}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/quotations`, payload, { withCredentials: true });
        toast.success('Quotation created');
      }
      setShowDialog(false); fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const submit = async (id) => { await axios.post(`${API}/quotations/${id}/submit`, {}, { withCredentials: true }); toast.success('Submitted for approval'); fetchData(); };
  const approve = async (id) => { await axios.post(`${API}/quotations/${id}/approve`, {}, { withCredentials: true }); toast.success('Approved'); fetchData(); };
  const reject = async () => {
    await axios.post(`${API}/quotations/${rejectingId}/reject`, { reason: rejectReason }, { withCredentials: true });
    toast.success('Rejected'); setRejectingId(null); setRejectReason(''); fetchData();
  };
  const duplicate = async (id) => { await axios.post(`${API}/quotations/${id}/duplicate`, {}, { withCredentials: true }); toast.success('Duplicated as draft'); fetchData(); };
  const remove = async (id) => { await axios.delete(`${API}/quotations/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchData(); };
  const convertToLpo = async (id) => {
    try { await axios.post(`${API}/quotations/${id}/convert-to-lpo`, {}, { withCredentials: true }); toast.success('Converted to LPO'); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Convert failed'); }
  };
  const convertToInvoice = async (id) => {
    try { await axios.post(`${API}/quotations/${id}/convert-to-invoice`, {}, { withCredentials: true }); toast.success('Converted to Invoice'); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Convert failed'); }
  };
  const partyName = (q) => {
    if (q.direction === 'sales') return tenants.find(t => t.id === q.client_id)?.name || q.contact_name || '—';
    return suppliers.find(s => s.id === q.supplier_id)?.name || q.contact_name || '—';
  };

  return (
    <Layout>
      <div data-testid="quotations-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-amber-600" />Quotations
            </h1>
            <p className="text-sm text-muted-foreground">Sales quotes to clients and purchase quotes from suppliers — approval workflow with PDF.</p>
          </div>
          {canManage && (
            <Button data-testid="new-quote-btn" onClick={openNew} className="bg-amber-600 hover:bg-amber-700"><Plus className="w-4 h-4 mr-1" />New Quotation</Button>
          )}
        </div>

        {/* Direction tabs + filters */}
        <div className="flex flex-wrap gap-3 items-center bg-white border border-slate-200 rounded-lg p-3">
          <div className="flex gap-1 p-0.5 rounded-md bg-slate-100">
            {[{ k: 'sales', label: 'Sales (to clients)' }, { k: 'purchase', label: 'Purchase (from suppliers)' }].map(opt => (
              <button key={opt.k} data-testid={`direction-${opt.k}`} onClick={() => setDirection(opt.k)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition ${direction === opt.k ? 'bg-white text-amber-700 shadow' : 'text-slate-600'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="invoiced">Invoiced</SelectItem>
            </SelectContent>
          </Select>
          <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
          <Input data-testid="quote-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" placeholder="from" />
          <Input data-testid="quote-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" placeholder="to" />
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid gap-3">
            {items.length === 0 && (
              <Card className="p-12 text-center"><FileSignature className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No quotations yet</p></Card>
            )}
            {items.map(q => (
              <Card key={q.id} data-testid={`quote-card-${q.id}`} className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono text-slate-500">{q.quotation_no}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[q.status] || 'bg-slate-100'}`}>{q.status}</span>
                      {q.approved_by_name && <span className="text-[10px] text-emerald-700">✓ {q.approved_by_name}</span>}
                    </div>
                    <div className="font-semibold text-foreground truncate">{q.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {partyName(q)} · {formatCurrency(q.grand_total || 0, symbol)} ·
                      {' '}valid until {q.valid_until?.split('T')[0] || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.status === 'draft' && canManage && (
                      <Button size="sm" variant="outline" onClick={() => submit(q.id)} data-testid={`quote-submit-${q.id}`}><Clock className="w-3.5 h-3.5 mr-1" />Submit</Button>
                    )}
                    {q.status === 'submitted' && canApprove && (
                      <>
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-600" onClick={() => approve(q.id)} data-testid={`quote-approve-${q.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" className="text-rose-600 border-rose-600" onClick={() => setRejectingId(q.id)} data-testid={`quote-reject-${q.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
                      </>
                    )}
                    {q.status === 'approved' && canManage && q.direction === 'purchase' && !q.converted_lpo_id && (
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => convertToLpo(q.id)} data-testid={`quote-to-lpo-${q.id}`}><ArrowRight className="w-3.5 h-3.5 mr-1" />Convert to LPO</Button>
                    )}
                    {q.status === 'approved' && canManage && q.direction === 'sales' && !q.converted_invoice_id && (
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => convertToInvoice(q.id)} data-testid={`quote-to-invoice-${q.id}`}><ArrowRight className="w-3.5 h-3.5 mr-1" />Convert to Invoice</Button>
                    )}
                    <RowActions
                      testIdPrefix={`quote-${q.id}`}
                      onView={() => setViewing(q)}
                      onEdit={canManage && q.status === 'draft' ? () => openEdit(q) : undefined}
                      onDuplicate={canManage ? () => duplicate(q.id) : undefined}
                      onDownloadPdf={() => generateQuotationPDF(q, { company, partyName: partyName(q) })}
                      onDelete={canManage && q.status === 'draft' ? () => remove(q.id) : undefined}
                      deleteLabel={`quotation ${q.quotation_no}`}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Quotation' : 'New Quotation'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input data-testid="quote-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
                <div>
                  <Label>{draft.direction === 'sales' ? 'Client' : 'Supplier'}</Label>
                  <Select value={draft.direction === 'sales' ? draft.client_id : draft.supplier_id} onValueChange={(v) => setDraft({ ...draft, ...(draft.direction === 'sales' ? { client_id: v } : { supplier_id: v }) })}>
                    <SelectTrigger data-testid="quote-party-select"><SelectValue placeholder="Select party" /></SelectTrigger>
                    <SelectContent>
                      {(draft.direction === 'sales' ? tenants : suppliers).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Building</Label>
                  <Select value={draft.building_id} onValueChange={(v) => setDraft({ ...draft, building_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Contact name</Label><Input value={draft.contact_name || ''} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} /></div>
                <div><Label>Contact phone</Label><Input value={draft.contact_phone || ''} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })} /></div>
                <div><Label>Contact email</Label><Input value={draft.contact_email || ''} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })} /></div>
                <div><Label>Issue date</Label><Input type="date" value={draft.issue_date} onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })} /></div>
                <div><Label>Valid until</Label><Input type="date" value={draft.valid_until} onChange={(e) => setDraft({ ...draft, valid_until: e.target.value })} /></div>
              </div>
              <div><Label>Scope summary</Label><Textarea rows={2} value={draft.scope_summary || ''} onChange={(e) => setDraft({ ...draft, scope_summary: e.target.value })} placeholder="Brief overview of the work" /></div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Line items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="quote-add-line"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
                </div>
                <div className="space-y-2">
                  {draft.line_items.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`quote-line-${idx}`}>
                      <Input className="col-span-3" placeholder="Item" value={li.item_name} onChange={(e) => updateLine(idx, 'item_name', e.target.value)} />
                      <Input className="col-span-3" placeholder="Description" value={li.description || ''} onChange={(e) => updateLine(idx, 'description', e.target.value)} />
                      <Input className="col-span-1" type="number" placeholder="Qty" value={li.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                      <Input className="col-span-1" placeholder="Unit" value={li.unit} onChange={(e) => updateLine(idx, 'unit', e.target.value)} />
                      <Input className="col-span-1" type="number" placeholder="Rate" value={li.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} />
                      <Input className="col-span-1" type="number" placeholder="Disc%" value={li.discount_pct} onChange={(e) => updateLine(idx, 'discount_pct', e.target.value)} />
                      <Input className="col-span-1" type="number" placeholder="Tax%" value={li.tax_pct} onChange={(e) => updateLine(idx, 'tax_pct', e.target.value)} />
                      <Button type="button" size="sm" variant="ghost" className="col-span-1 text-red-500" onClick={() => removeLine(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
                  <div className="bg-slate-50 p-2 rounded"><div className="text-xs text-muted-foreground">Subtotal</div><div className="font-semibold">{formatCurrency(totals.subtotal, symbol)}</div></div>
                  <div className="bg-slate-50 p-2 rounded"><div className="text-xs text-muted-foreground">Discount</div><div className="font-semibold">{formatCurrency(totals.discount, symbol)}</div></div>
                  <div className="bg-slate-50 p-2 rounded"><div className="text-xs text-muted-foreground">Tax</div><div className="font-semibold">{formatCurrency(totals.tax, symbol)}</div></div>
                  <div className="bg-amber-50 p-2 rounded border border-amber-200"><div className="text-xs text-amber-800">Grand Total</div><div className="font-bold text-amber-900">{formatCurrency(totals.grand, symbol)}</div></div>
                </div>
              </div>

              <div><Label>Terms & conditions</Label><Textarea rows={3} value={draft.terms || ''} onChange={(e) => setDraft({ ...draft, terms: e.target.value })} /></div>
              <Button onClick={save} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-quote-btn">{editing ? 'Update Quotation' : 'Create Quotation'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View dialog */}
        <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{viewing?.title}</DialogTitle></DialogHeader>
            {viewing && (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><div className="text-xs text-muted-foreground">Quote #</div><div className="font-mono">{viewing.quotation_no}</div></div>
                  <div><div className="text-xs text-muted-foreground">Status</div><div className="capitalize"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[viewing.status]}`}>{viewing.status}</span></div></div>
                  <div><div className="text-xs text-muted-foreground">Party</div><div>{partyName(viewing)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Building</div><div>{buildings.find(b => b.id === viewing.building_id)?.name || '—'}</div></div>
                  <div><div className="text-xs text-muted-foreground">Issue / Valid</div><div>{viewing.issue_date?.split('T')[0]} → {viewing.valid_until?.split('T')[0]}</div></div>
                  <div><div className="text-xs text-muted-foreground">Total</div><div className="font-bold">{formatCurrency(viewing.grand_total || 0, symbol)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Prepared by</div><div>{viewing.prepared_by_name || '—'}</div></div>
                  <div><div className="text-xs text-muted-foreground">Approved by</div><div>{viewing.approved_by_name || '—'} {viewing.approved_at ? `· ${viewing.approved_at.split('T')[0]}` : ''}</div></div>
                </div>
                {viewing.scope_summary && (<div><div className="text-xs text-muted-foreground">Scope</div><div className="whitespace-pre-wrap">{viewing.scope_summary}</div></div>)}
                <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs border border-slate-200 rounded min-w-[520px]">
                  <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Item</th><th>Qty</th><th>Rate</th><th>Disc%</th><th>Tax%</th><th>Total</th></tr></thead>
                  <tbody>
                    {(viewing.line_items || []).map((li, i) => (
                      <tr key={i} className="border-t"><td className="px-2 py-1">{li.item_name}</td><td className="text-center">{li.quantity}</td><td className="text-right">{li.unit_price}</td><td className="text-center">{li.discount_pct}</td><td className="text-center">{li.tax_pct}</td><td className="text-right">{((li.quantity || 0) * (li.unit_price || 0) * (1 - (li.discount_pct || 0) / 100) * (1 + (li.tax_pct || 0) / 100)).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => generateQuotationPDF(viewing, { company, partyName: partyName(viewing) })} data-testid="quote-view-pdf-btn">Download PDF</Button>
                  <Button onClick={() => setViewing(null)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject dialog */}
        <Dialog open={!!rejectingId} onOpenChange={(v) => { if (!v) { setRejectingId(null); setRejectReason(''); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reject quotation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Textarea data-testid="quote-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Reason for rejection (optional)" />
              <Button onClick={reject} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="quote-reject-confirm">Confirm reject</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
